/**
 * AgentTab — Lo que baja a los equipos del agente local (PLAN_AGENTE_LOCAL F4.3): los mapeos del
 * sistema de gestión del cliente (cómo leer su TXT) y las reglas de impresión (qué se imprime,
 * dónde y cuántas veces). Un cambio acá llega a todos los equipos en su siguiente sincronización.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Download, FileUp, Pencil, Plus, Trash2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { usePermission } from '@/hooks/usePermission'
import { useToast, ToastContainer } from '@/components/Toast'
import { Button } from '@/components/Button'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { DataTable } from '@/components/ds/DataTable'
import { Modal } from '@/components/ds/Modal'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { EmptyState } from '@/components/EmptyState'
import { extractErrorMessage, extractFieldErrors } from '@/lib/errorUtils'
import { fmtDateTime } from '@/lib/utils'
import { usePrintTemplates } from '@/modules/config/hooks/usePrintTemplates'
import { TIPOS_POR_CLASE, nombreDelTipo } from '../tiposDeDocumento'
import InstaladorCard from './InstaladorCard'
import {
  downloadAgentMapping, useAgentMappings, useAgentWarnings, useDeleteAgentMapping,
  useDeletePrintRule, usePosTerminals, usePrintRules, useSavePrintRule, useUploadAgentMapping,
  type AgentMapping, type PrintRule,
} from '../hooks/usePos'

const TIPOS = TIPOS_POR_CLASE.agente

export default function AgentTab() {
  const { can } = usePermission()
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()
  const puedeEditar = can('pos.terminals', 'edit')
  const { data: mapeos = [], isLoading: cargandoMapeos } = useAgentMappings()
  const { data: reglas = [], isLoading: cargandoReglas } = usePrintRules()
  const { data: plantillas = [] } = usePrintTemplates()
  const quitarMapeo = useDeleteAgentMapping()
  const quitarRegla = useDeletePrintRule()
  const [subiendo, setSubiendo] = useState<Partial<AgentMapping> | null>(null)
  const [editando, setEditando] = useState<Partial<PrintRule> | null>(null)

  const nombreDePlantilla = useMemo(() => {
    const m = new Map<string, string>()
    plantillas.filter((p) => p.status === 'published').forEach((p) => m.set(p.family_id, p.name))
    return m
  }, [plantillas])

  const pedirQuitarMapeo = (m: AgentMapping) => confirm({
    message: `¿Quitar el mapeo ${m.device_file_name}? Los equipos dejan de leer los TXT de ese tipo`
      + (m.variant ? ' y variante' : '') + ' hasta que se suba otro.',
    confirmLabel: 'Quitar',
    onConfirm: async () => {
      try {
        await quitarMapeo.mutateAsync(m.id)
        toast.success('Mapeo quitado')
      } catch (e) {
        toast.error(extractErrorMessage(e, 'No se pudo quitar el mapeo.'))
      }
    },
  })

  const pedirQuitarRegla = (r: PrintRule) => confirm({
    message: `¿Quitar la regla «${r.name}»? Para dejar de usarla por un tiempo basta con desactivarla.`,
    confirmLabel: 'Quitar',
    onConfirm: async () => {
      try {
        await quitarRegla.mutateAsync(r.id)
        toast.success('Regla quitada')
      } catch (e) {
        toast.error(extractErrorMessage(e, 'No se pudo quitar la regla.'))
      }
    },
  })

  const descargar = async (m: AgentMapping) => {
    try {
      await downloadAgentMapping(m)
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo descargar el mapeo.'))
    }
  }

  return (
    <div className="space-y-8">
      <InstaladorCard
        programa="conector"
        titulo="Instalar Clobi Conector"
        pasos={[
          <>Crear el terminal en <strong>Terminales</strong>, clase Clobi Conector, y generar su código (ícono de llave).</>,
          'Ejecutar el instalador en el equipo Windows donde el sistema de gestión deja los TXT (Windows 7 SP1 o posterior).',
          'Al terminar se abre la configuración: dirección de la empresa, código, carpeta de los TXT e impresoras.',
        ]}
        nota={<>Si Windows muestra «Windows protegió su PC», elegir «Más información» y «Ejecutar de todas formas».
          El manual trae los requisitos, lo que se prepara en Clobi, el paso desde FacturaDesktop y los problemas frecuentes.</>}
        onError={(e) => toast.error(extractErrorMessage(e, 'No se pudo descargar el instalador.'))}
      />
      <CondicionesQueNoEvalua />

      <section>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h2 className="text-base font-semibold text-content">Mapeos del sistema de gestión</h2>
            <p className="text-sm text-content-muted">
              Cómo leer el TXT de cada tipo de documento. Un mapeo nuevo reemplaza al del mismo tipo y variante.
            </p>
          </div>
          {puedeEditar && (
            <Button onClick={() => setSubiendo({ document_type_code: 33, variant: '' })}>
              <FileUp size={14} /> Subir mapeo
            </Button>
          )}
        </div>
        <DataTable<AgentMapping>
          rows={mapeos}
          rowKey={(m) => m.id}
          loading={cargandoMapeos}
          empty={<EmptyState title="No hay mapeos." size="sm"
            hint="Sin el mapeo de un tipo, el conector deja sus TXT en la carpeta de errores." />}
          columns={[
            { key: 'type', header: 'Documento', render: (m) => nombreDelTipo(m.document_type_code) },
            { key: 'variant', header: 'Variante', render: (m) => m.variant || <span className="text-content-subtle">—</span> },
            { key: 'device', header: 'Archivo en el equipo', render: (m) => <span className="font-mono text-xs">{m.device_file_name}</span> },
            { key: 'file', header: 'Archivo subido', render: (m) => <span className="text-xs text-content-muted">{m.file_name}</span> },
            { key: 'updated', header: 'Actualizado', render: (m) => fmtDateTime(m.updated_at) },
            {
              key: 'actions', header: '',
              render: (m) => (
                <div className="flex justify-end gap-1">
                  <button onClick={() => descargar(m)} title="Descargar" aria-label={`Descargar ${m.device_file_name}`}
                    className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted">
                    <Download size={14} />
                  </button>
                  {puedeEditar && (
                    <button onClick={() => pedirQuitarMapeo(m)} title="Quitar" aria-label={`Quitar ${m.device_file_name}`}
                      className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-danger-content">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </section>

      <section>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h2 className="text-base font-semibold text-content">Reglas de impresión</h2>
            <p className="text-sm text-content-muted">
              Se aplican todas las reglas activas que se cumplen. La impresora es un nombre: en cada equipo se elige
              a qué impresora de Windows corresponde.
            </p>
          </div>
          {puedeEditar && (
            <Button onClick={() => setEditando({ document_types: [], copies: 1, cedible_copies: 0, priority: 100, is_active: true })}>
              <Plus size={14} /> Nueva regla
            </Button>
          )}
        </div>
        <DataTable<PrintRule>
          rows={reglas}
          rowKey={(r) => r.id}
          loading={cargandoReglas}
          empty={<EmptyState title="No hay reglas de impresión." size="sm"
            hint="Sin reglas, el conector emite y sube los documentos pero no imprime." />}
          columns={[
            { key: 'priority', header: 'Orden', numeric: true, render: (r) => r.priority },
            { key: 'name', header: 'Nombre' },
            { key: 'terminal', header: 'Equipo', render: (r) => r.terminal_code || 'Todos' },
            {
              key: 'types', header: 'Documentos',
              render: (r) => (r.document_types.length ? r.document_types.join(', ') : 'Todos'),
            },
            {
              key: 'when', header: 'Cuándo',
              render: (r) => {
                const partes = [r.receiver_tax_id && `Receptor ${r.receiver_tax_id}`, r.condition].filter(Boolean)
                return partes.length
                  ? <span className="font-mono text-xs">{partes.join(' · ')}</span>
                  : <span className="text-content-subtle">Siempre</span>
              },
            },
            { key: 'printer', header: 'Impresora', render: (r) => r.logical_printer },
            {
              key: 'copies', header: 'Copias',
              render: (r) => (r.cedible_copies ? `${r.copies} + ${r.cedible_copies} cedible` : String(r.copies)),
            },
            {
              key: 'template', header: 'Plantilla',
              render: (r) => (r.template_family_id
                ? nombreDePlantilla.get(r.template_family_id) ?? 'Sin versión publicada'
                : <span className="text-content-subtle">La del tipo</span>),
            },
            {
              key: 'status', header: 'Estado',
              render: (r) => (
                <StatusBadge status={r.is_active ? 'active' : 'inactive'} label={r.is_active ? 'Activa' : 'Desactivada'}
                  tone={r.is_active ? 'success' : 'neutral'} />
              ),
            },
            {
              key: 'actions', header: '',
              render: (r) => puedeEditar && (
                <div className="flex justify-end gap-1">
                  <button onClick={() => setEditando(r)} title="Editar" aria-label={`Editar ${r.name}`}
                    className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => pedirQuitarRegla(r)} title="Quitar" aria-label={`Quitar ${r.name}`}
                    className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-danger-content">
                    <Trash2 size={14} />
                  </button>
                </div>
              ),
            },
          ]}
        />
      </section>

      {subiendo && (
        <MappingModal
          onClose={() => setSubiendo(null)}
          onSaved={(reemplazo) => {
            toast.success(reemplazo ? 'Mapeo reemplazado' : 'Mapeo subido')
            setSubiendo(null)
          }}
        />
      )}
      {editando && (
        <RuleModal
          initial={editando}
          plantillas={[...nombreDePlantilla.entries()].map(([value, label]) => ({ value, label }))}
          onClose={() => setEditando(null)}
          onSaved={(nueva) => {
            toast.success(nueva ? 'Regla creada' : 'Regla actualizada')
            setEditando(null)
          }}
        />
      )}
      <ConfirmModal {...modalProps} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

function MappingModal({ onClose, onSaved }: { onClose: () => void; onSaved: (reemplazo: boolean) => void }) {
  const subir = useUploadAgentMapping()
  const entrada = useRef<HTMLInputElement>(null)
  const [tipo, setTipo] = useState('33')
  const [variante, setVariante] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const enviar = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!archivo) {
      setError('Falta elegir el archivo del mapeo.')
      return
    }
    subir.mutate({ file: archivo, documentTypeCode: Number(tipo), variant: variante.trim() }, {
      onSuccess: ({ replaced }) => onSaved(replaced),
      onError: (err) => setError(extractErrorMessage(err, 'No se pudo subir el mapeo.')),
    })
  }

  const destino = variante.trim() ? `${tipo}_${variante.trim()}_Template.xml` : `${tipo}_Template.xml`

  return (
    <Modal open onClose={onClose} title="Subir mapeo"
      subtitle="El XML que describe el TXT del sistema de gestión para un tipo de documento."
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="pos-agent-mapping-form" isPending={subir.isPending}>Subir</Button>
        </>
      }>
      <form id="pos-agent-mapping-form" onSubmit={enviar} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField dense label="Documento" required value={tipo} onChange={(e) => setTipo(e.target.value)}
            options={TIPOS.map((c) => ({ value: String(c), label: nombreDelTipo(c) }))} />
          <TextField dense label="Variante" placeholder="Vacía si es el mapeo del tipo" value={variante}
            onChange={(e) => setVariante(e.target.value)}
            hint="La que aparece en el nombre del TXT, por ejemplo NPG en 33_NPG_…" />
        </div>
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-content-muted">Archivo</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => entrada.current?.click()}>Elegir…</Button>
            <span className="text-sm text-content truncate">{archivo?.name ?? 'Sin archivo'}</span>
          </div>
          <input ref={entrada} type="file" accept=".xml,text/xml,application/xml" className="hidden"
            aria-label="Archivo del mapeo" onChange={(e) => setArchivo(e.target.files?.[0] ?? null)} />
        </div>
        <p className="text-xs text-content-subtle">
          En los equipos queda como <span className="font-mono">{destino}</span>.
        </p>
        {error && <p role="alert" className="text-sm text-danger-content">{error}</p>}
      </form>
    </Modal>
  )
}

function RuleModal({ initial, plantillas, onClose, onSaved }: {
  initial: Partial<PrintRule>
  plantillas: { value: string; label: string }[]
  onClose: () => void
  onSaved: (nueva: boolean) => void
}) {
  const { register, handleSubmit, reset, setError, watch, setValue, formState: { errors } } = useForm<Partial<PrintRule>>()
  const { data: terminales = [] } = usePosTerminals()
  const { mutate, isPending } = useSavePrintRule()
  const [error, setGeneral] = useState<string | null>(null)
  const tipos = watch('document_types') || []
  const agentes = terminales.filter((t) => t.kind === 'agente')

  useEffect(() => { reset(initial) }, [initial, reset])

  const alternar = (code: number) => setValue(
    'document_types',
    tipos.includes(code) ? tipos.filter((c) => c !== code) : [...tipos, code],
  )

  const onSubmit = (data: Partial<PrintRule>) => {
    setGeneral(null)
    mutate({
      ...data,
      id: initial.id,
      terminal: data.terminal || null,
      template_family_id: data.template_family_id || null,
    }, {
      onSuccess: () => onSaved(!initial.id),
      onError: (e) => {
        const campos = extractFieldErrors(e)
        // Con el error junto a su campo, el mensaje general sobra.
        if (campos) Object.entries(campos).forEach(([k, m]) => setError(k as keyof PrintRule, { message: m }))
        else setGeneral(extractErrorMessage(e, 'No se pudo guardar la regla.'))
      },
    })
  }

  const numero = { setValueAs: (v: string) => (v === '' ? undefined : Number(v)) }

  return (
    <Modal open onClose={onClose} size="lg" title={initial.id ? 'Editar regla de impresión' : 'Nueva regla de impresión'}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="pos-print-rule-form" isPending={isPending}>Guardar</Button>
        </>
      }>
      <form id="pos-print-rule-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
          <TextField dense label="Nombre" required placeholder="Facturas en la láser"
            inputProps={register('name', { required: 'El nombre es obligatorio.' })} error={errors.name?.message} />
          <TextField dense label="Orden" hint="Las de menor número se imprimen primero."
            inputProps={{ ...register('priority', numero), inputMode: 'numeric' }} error={errors.priority?.message} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField dense label="Equipo" placeholder="Todos los equipos"
            options={agentes.map((t) => ({ value: t.id, label: `${t.code} · ${t.name}` }))}
            selectProps={register('terminal')} error={errors.terminal?.message} />
          <TextField dense label="Impresora" required placeholder="Facturas"
            hint="Un nombre; en cada equipo se elige qué impresora de Windows es."
            inputProps={register('logical_printer', { required: 'La impresora es obligatoria.' })}
            error={errors.logical_printer?.message} />
        </div>
        <div>
          <p className="text-xs font-medium text-content-muted mb-1.5">Documentos (ninguno marcado: todos)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
            {TIPOS.map((code) => (
              <label key={code} className="flex items-center gap-2 text-sm text-content">
                <input type="checkbox" checked={tipos.includes(code)} onChange={() => alternar(code)} />
                {nombreDelTipo(code)}
              </label>
            ))}
          </div>
          {errors.document_types?.message && <p className="text-xs text-danger-content mt-1">{errors.document_types.message}</p>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField dense label="RUT del receptor" placeholder="Cualquier receptor"
            inputProps={register('receiver_tax_id')} error={errors.receiver_tax_id?.message} />
          <SelectField dense label="Plantilla" placeholder="La publicada del tipo de documento"
            options={plantillas} selectProps={register('template_family_id')} error={errors.template_family_id?.message} />
        </div>
        <TextField dense label="Condición" placeholder="Sin condición: siempre"
          hint="Lo que evalúa el conector: tipo, variante, cedible, campo('Ruta'), comparaciones, in, and, or, not. campo() entrega texto: las guías de traslado interno son campo('Encabezado/IdDoc/IndTraslado') == '5'."
          inputProps={register('condition')} error={errors.condition?.message} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField dense label="Copias"
            inputProps={{ ...register('copies', numero), inputMode: 'numeric' }} error={errors.copies?.message} />
          <TextField dense label="Copias cedibles" hint="Solo las facturas y guías tienen copia cedible."
            inputProps={{ ...register('cedible_copies', numero), inputMode: 'numeric' }}
            error={errors.cedible_copies?.message} />
        </div>
        <label className="flex items-center gap-2 text-sm text-content">
          <input type="checkbox" {...register('is_active')} />
          Activa
        </label>
        {error && <p className="text-sm text-danger-content">{error}</p>}
      </form>
    </Modal>
  )
}

/**
 * Las condiciones de las plantillas de los equipos que el conector no evalúa: en el equipo el elemento no
 * se dibuja (la condición cuenta como no cumplida). No se bloquean: la plantilla también sirve al PDF de la web.
 */
function CondicionesQueNoEvalua() {
  const { data: avisos = [] } = useAgentWarnings()
  if (avisos.length === 0) return null
  return (
    <section role="status" className="rounded-xl border border-warning-border bg-warning-surface p-4 text-sm text-warning-content">
      <div className="flex items-center gap-2 font-semibold">
        <AlertTriangle size={16} />
        {avisos.length === 1 ? 'Una condición de plantilla que Clobi Conector no evalúa'
          : `${avisos.length} condiciones de plantillas que Clobi Conector no evalúa`}
      </div>
      <p className="mt-1">
        En el equipo ese elemento no se imprime, aunque en el PDF de Clobi sí aparezca. Se corrige en el editor de plantillas
        usando solo tipo, variante, cedible, campo(), comparaciones, in, and, or y not.
      </p>
      <ul className="mt-2 space-y-1">
        {avisos.map((a, i) => (
          <li key={`${a.template_family_id}-${i}`}>
            <strong>{a.template_name}</strong> · {a.where}: <code className="font-mono text-xs">{a.condition}</code> — no evalúa {a.reason}.
          </li>
        ))}
      </ul>
    </section>
  )
}
